import { http } from "../../../shared/api/http"

export type LessonType = "text" | "video"
export type LessonAccessType = "free" | "paid"

export type LessonPayload = {
  module_id: string
  title: string

  type?: LessonType
  access_type?: LessonAccessType
  content?: string | null

  video_id?: string | null
  duration?: string | null

  order_number?: number | null
  is_active?: boolean

  file_ids?: string[]
}

export type LessonItem = {
  id: string
  module_id: string
  title: string
  type: LessonType
  access_type: LessonAccessType
  order_number: number
  is_active: boolean
}

export type LessonListItem = {
  id: string
  title: string
  type: string
  access_type: string
  duration: string
  order_number: number
}

export type LessonListResponse = {
  items: LessonListItem[]
}

export type LessonDetail = {
  id: string
  module_id: string
  title: string
  type: string
  content: string
  duration: string
  order_number: number
  access_type: string
  is_active?: boolean
  files: any[]
}

export const createLessonApi = (data: LessonPayload) =>
  http.post<{ id: string }>("api/admin/lessons/create", data)

export const updateLessonApi = (
  id: string,
  data: Partial<LessonPayload>
) =>
  http.patch<{ id: string }>(`api/admin/lessons/${id}/update`, { id, ...data })

export const deleteLessonApi = (id: string) =>
  http.delete<{ success: boolean }>(
    `api/admin/lessons/${id}/delete`
  )

/** Mentor content editing — no access_type / is_active (admin-only fields). */
export type MentorLessonUpdatePayload = {
  title?: string
  type?: LessonType
  content?: string | null
  video_id?: string | null
  duration?: string | null
  order_number?: number
}

export const updateMentorLessonApi = (
  id: string,
  data: MentorLessonUpdatePayload
) =>
  http.patch<LessonDetail>(`/api/mentor/lessons/${id}`, data)

export const listLessonsApi = (moduleId: string) =>
  http.get<LessonListResponse>(
    `/api/modules/${moduleId}/lessons`
  )

export const getLessonApi = (
  moduleId: string,
  lessonId: string
) =>
  http.get<LessonDetail>(
    `/api/modules/${moduleId}/lessons/${lessonId}`
  )

/** Admin-only lesson lookup — keyed by lesson_id alone (no module_id needed). Includes video_id, unlike the module-scoped endpoint above; does not include file attachments. */
export type AdminLessonDetail = {
  id: string
  module_id: string
  title: string
  type: string
  access_type: string
  content: string | null
  video_id: string | null
  duration: string | null
  order_number: number
  is_active: boolean
  files?: LessonFile[]
}

export const getAdminLessonApi = (lessonId: string) =>
  http.get<AdminLessonDetail>(`/api/admin/lessons/${lessonId}`)
export type LessonFile = {
  id: string
  file_id?: string
  url: string
  filename: string
  size: number
  kind?: string
}

/* ===== Video upload (Bunny Stream, direct-to-cloud via tus) ===== */

export type LessonVideoInitResponse = {
  video_id: string
  tus_endpoint: string
  headers: Record<string, string | number>
}

export const initLessonVideoUploadApi = (lessonId: string, title?: string) =>
  http.post<LessonVideoInitResponse>(
    `/api/admin/lessons/${lessonId}/video/init`,
    title ? { title } : {}
  )

export type LessonVideoStatus =
  | "created"
  | "uploaded"
  | "processing"
  | "transcoding"
  | "finished"
  | "error"
  | "upload_failed"

export type LessonVideoStatusResponse = {
  video_id: string
  status: LessonVideoStatus
  encode_progress: number
  is_ready: boolean
}

export const getLessonVideoStatusApi = (lessonId: string, videoId: string) =>
  http.get<LessonVideoStatusResponse>(
    `/api/admin/lessons/${lessonId}/video/${videoId}/status`
  )

export type LessonVideoStream = {
  embed_url: string
  expires_at: number
}

/** Admin preview stream — no course-purchase check, unlike the student-facing endpoint. */
export const getAdminLessonVideoStreamApi = (lessonId: string) =>
  http.get<LessonVideoStream>(`/api/admin/lessons/${lessonId}/video/stream`)

export const uploadLessonFileApi = (file: File) => {
  const formData = new FormData()
  formData.append("file", file)

  return http.post<LessonFile>(
    "/api/admin/lessons/upload/file",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  )
}
